import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_BRAND_COLORS, migrateLegacyThemeColors } from '../constants/brandColors';

export type ThemeMode = 'light' | 'dark';

interface ThemeColors {
  primary: string;
  secondary: string;
}

interface ThemeStore {
  colors: ThemeColors;
  mode: ThemeMode;
  setColors: (colors: ThemeColors) => void;
  setMode: (mode: ThemeMode) => void;
  resetColors: () => void;
}

const DEFAULT_COLORS: ThemeColors = {
  primary: DEFAULT_BRAND_COLORS.primary,
  secondary: DEFAULT_BRAND_COLORS.secondary,
};

// Função para obter cores iniciais do establishment se existir no localStorage
const getInitialColors = (): ThemeColors => {
  try {
    const establishmentStorage = localStorage.getItem('establishment-storage');
    if (establishmentStorage) {
      const parsed = JSON.parse(establishmentStorage);
      const establishment = parsed?.state?.establishment;
      if (establishment?.primaryColor && establishment?.secondaryColor) {
        return {
          primary: establishment.primaryColor,
          secondary: establishment.secondaryColor,
        };
      }
    }
  } catch (error) {
    console.error('Erro ao carregar cores do establishment:', error);
  }
  return DEFAULT_COLORS;
};

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set) => ({
      colors: getInitialColors(),
      mode: 'dark',
      setColors: (colors) => set({ colors }),
      setMode: (mode) => set({ mode }),
      resetColors: () => set({ colors: DEFAULT_COLORS }),
    }),
    {
      name: 'theme-storage',
      version: 5,
      partialize: (state) => ({ colors: state.colors, mode: state.mode }),
      migrate: (persistedState: unknown, _version: number) => {
        const s = persistedState as { colors?: ThemeColors; mode?: ThemeMode | 'system' };
        const raw = s?.colors ?? getInitialColors();
        const colors = migrateLegacyThemeColors(raw.primary, raw.secondary);
        return {
          colors,
          mode: (s?.mode === 'system' ? 'dark' : s?.mode) ?? 'dark',
        };
      },
    }
  )
);
